/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Reason: these values had been redeclared in ~12 files with two spellings
 * of the same number (`5 * 60 * 1000` and `300_000`), so changing
 * the cache policy meant touching all those places and risked
 * leaving divergent values. Import from here instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations.*/
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by foreground reminders window.*/

/** Minutes a cached datum is considered fresh (offline cache and session).*/
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally via PowerSync, so a generous `staleTime`
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * showing stale data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (even though it currently matches the
 * offline-cache value) because it is an independent decision: it must stay aligned
 * between `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * HARD CEILING for `get-session` AWAITED inside a `beforeLoad` (I1-bis).
 *
 * No route guard should ever wait indefinitely on the network: if it does,
 * the route NEVER resolves, the `<Suspense fallback={null}>` in `__root.tsx`
 * renders nothing and the ENTIRE application stays blank. And this is not a
 * lab scenario: a SLOW response (delayed, not aborted) triggers it. With a
 * 401 or 5xx the promise resolves and nothing strange is seen — which is why
 * I1 originally guarded against the wrong failure mode and the green matrix cell
 * was measuring something else.
 *
 * Measured in this repo (2026-08-01, cold guest start, `get-session` throttled):
 * before this change, `document.body.innerText` was `""` at 3/6/10/15/20 s on `/`
 * and `/login`, in both dev server **and production bundle**.
 *
 * Once the deadline expires, the app proceeds without a session: an eternal blank
 * screen is far worse than a login form that might not have been needed.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (title and notes in
 * detail, subtasks). Balances typing responsiveness with mutation volume.
 */

/** Seconds before revoking a download's object URL (see constant).*/
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably longer than the browser takes to consume the href
 * of the `<a download>`; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
